import { useCallback } from 'react';
import { canvasSelectionStore } from '../stores/canvasSelectionStore';
import { GRID_SIZE } from '../types';
import { dropNodesIntoGroup } from '../lib/groupMembership';
import { buildBulkDragContext } from '../lib/buildBulkDragContext';
import { computeAlignedMoves } from '../lib/computeAlignedMoves';
import { applyImperativeDragDom } from '../lib/applyImperativeDragDom';
import { resolveGroupDragPayload } from '../lib/resolveGroupDragPayload';
import { useCanvasStores, useCanvasRotation } from '../stores/CanvasContext';
import { globalTasksStore } from '../../stores/globalTasksStore';

export function useCanvasDragHandlers() {
  const { store, dragStore, measureMapRef, txManager } = useCanvasStores();
  const rotation = useCanvasRotation();

  const handleDropIntoGroup = useCallback((draggedNodeIds: string[]) => {
    if (draggedNodeIds.length === 0) return;
    // Use txManager.getContext() so that during a deferred transaction
    // we see the updated positions (not the stale store state)
    dropNodesIntoGroup(store, draggedNodeIds, txManager.getContext());
  }, [store, txManager]);

  // Unified bulk drag: uses split entity maps to resolve start positions for ALL entity kinds
  const startBulkDrag = useCallback((
    dragEntityIds: string[],
    dragCollapsedGroupIds: string[],
    e: React.PointerEvent,
    onDragEnd?: (dragNodeIds: string[]) => void,
    dragExpandedGroupIds: string[] = [],
  ) => {
    // Begin deferred transaction: all drag moves are one undo step,
    // and store updates are batched until commit() → single React render on pointerup
    txManager.begin('drag', true);

    const ctx = buildBulkDragContext(store, dragEntityIds, dragCollapsedGroupIds, dragExpandedGroupIds, measureMapRef);

    const startScreenX = e.clientX;
    const startScreenY = e.clientY;
    const canvasEl = (e.target as HTMLElement).closest('[data-canvas-canvas]') as HTMLElement | null;
    const zoom = canvasEl ? parseFloat(canvasEl.dataset.zoom ?? '1') : 1;
    const rot = rotation;
    let didMove = false;

    // Track last computed positions for final commit
    let lastMoves: Array<{ id: string; x: number; y: number }> = [];
    let lastGroupMoves: Array<{ groupId: string; collapsedX: number; collapsedY: number }> = [];

    function handlePointerMove(ev: PointerEvent) {
      const rawDx = (ev.clientX - startScreenX) / zoom;
      const rawDy = (ev.clientY - startScreenY) / zoom;

      let dx = rawDx;
      let dy = rawDy;
      if (rot !== 0) {
        const rad = (-rot * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        dx = rawDx * cos - rawDy * sin;
        dy = rawDx * sin + rawDy * cos;
      }

      let snapDx = 0;
      let snapDy = 0;

      if (ctx.entityStarts.length > 0) {
        const result = computeAlignedMoves(
          ctx.entityStarts, dx, dy,
          ctx.draggedAlignIds, ctx.allEntities, ctx.entityMap, ctx.getEffectiveDims,
        );
        lastMoves = result.moves;
        snapDx = result.snapDx;
        snapDy = result.snapDy;
        dragStore.send({ type: 'alignmentGuides.set', guides: result.guides });

        if (!didMove && lastMoves.some((m, i) => m.x !== ctx.entityStarts[i].startX || m.y !== ctx.entityStarts[i].startY)) {
          didMove = true;
        }
      }

      // Compute collapsed group moves
      if (ctx.groupStarts.length > 0) {
        lastGroupMoves = ctx.groupStarts.map(sp => ({
          groupId: sp.groupId,
          collapsedX: Math.round((sp.startX + dx + snapDx) / GRID_SIZE) * GRID_SIZE,
          collapsedY: Math.round((sp.startY + dy + snapDy) / GRID_SIZE) * GRID_SIZE,
        }));
      }

      applyImperativeDragDom(ctx, lastMoves, lastGroupMoves);
    }

    function handlePointerUp() {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      dragStore.send({ type: 'alignmentGuides.set', guides: [] });

      if (didMove) {
        // Final commit: ONE store update → ONE React render
        if (lastMoves.length > 0) {
          store.send({ type: 'entity.batchMove', moves: lastMoves });
        }
        if (lastGroupMoves.length > 0) {
          store.send({ type: 'group.batchMoveCollapsed', moves: lastGroupMoves });
        }
        onDragEnd?.(ctx.draggedNodeIds);
      }

      txManager.commit();
    }

    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
  }, [store, rotation, dragStore, measureMapRef, txManager]);

  // Generic entity drag: works for nodes, waypoints, textblocks, textlabels
  const handleEntityDragStart = useCallback((entityId: string, e: React.PointerEvent) => {
    const sel = canvasSelectionStore.getSnapshot().context;
    const isInSelection = sel.selectedEntityIds.includes(entityId);

    if (!isInSelection) {
      canvasSelectionStore.send({ type: 'entity.selectOne', entityId });
    }

    const dragIds = isInSelection ? sel.selectedEntityIds : [entityId];
    startBulkDrag(dragIds, [], e, handleDropIntoGroup);
  }, [startBulkDrag, handleDropIntoGroup]);

  // Node drag — delegates to generic handler
  const handleDragStart = useCallback((taskId: string, e: React.PointerEvent) => {
    handleEntityDragStart(taskId, e);
  }, [handleEntityDragStart]);

  // Waypoint drag — delegates to generic handler
  const handleWaypointDragStart = useCallback((waypointId: string, e: React.PointerEvent) => {
    handleEntityDragStart(waypointId, e);
  }, [handleEntityDragStart]);

  // Group drag — reads groups inline from store snapshot (no subscription needed)
  const handleGroupDragStart = useCallback((groupId: string, e: React.PointerEvent) => {
    const ctx = store.getSnapshot().context;
    const rawEntities = [
      ...Object.values(ctx.nodes),
      ...Object.values(ctx.connectors),
      ...Object.values(ctx.groups),
    ];
    const tasksById = globalTasksStore.getSnapshot().context.tasksById;
    const sel = canvasSelectionStore.getSnapshot().context;

    const payload = resolveGroupDragPayload(groupId, sel.selectedEntityIds, rawEntities, tasksById);
    startBulkDrag(payload.entityIds, payload.collapsedGroupIds, e, undefined, payload.expandedGroupIds);
  }, [store, startBulkDrag]);

  return {
    handleDragStart,
    handleEntityDragStart,
    handleWaypointDragStart,
    handleGroupDragStart,
  };
}
